import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
    saveTokens, getAccessToken, clearTokens, api,
    loginApi, registerApi, fetchCurrentUser, refreshAccessToken, logoutApi,
    formatApiErrorDetail
} from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const tryRefresh = useCallback(async () => {
        try {
            const newToken = await refreshAccessToken();
            if (!newToken) return false;
            saveTokens(newToken, null);
            const userData = await fetchCurrentUser();
            setUser(userData);
            return true;
        } catch {
            return false;
        }
    }, []);

    const checkAuth = useCallback(async () => {
        if (!getAccessToken()) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const userData = await fetchCurrentUser();
            setUser(userData);
        } catch {
            const refreshed = await tryRefresh();
            if (!refreshed) {
                clearTokens();
                setUser(false);
            }
        } finally {
            setLoading(false);
        }
    }, [tryRefresh]);

    useEffect(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // If returning from Emergent Google Auth, AuthCallback will handle the token exchange.
        // Skip the /auth/me check to avoid a 401 race before the new tokens are saved.
        if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        try {
            const data = await loginApi(email, password);
            saveTokens(data.access_token, data.refresh_token);
            // Fetch full user (with limits + usage) instead of only login payload
            try {
                const full = await fetchCurrentUser();
                setUser(full);
            } catch {
                const { access_token, refresh_token, ...userData } = data;
                setUser(userData);
            }
            return { success: true };
        } catch (error) {
            const detail = formatApiErrorDetail(error.response?.data?.detail);
            const fallback = error.response ? "Invalid email or password" : "Could not connect to server. Please try again.";
            return { success: false, error: detail || fallback };
        }
    }, []);

    const register = useCallback(async (name, email, password, role = 'student') => {
        try {
            const data = await registerApi(name, email, password, role);
            saveTokens(data.access_token, data.refresh_token);
            try {
                const full = await fetchCurrentUser();
                setUser(full);
            } catch {
                const { access_token, refresh_token, ...userData } = data;
                setUser(userData);
            }
            return { success: true };
        } catch (error) {
            const detail = formatApiErrorDetail(error.response?.data?.detail);
            const fallback = error.response ? "Registration failed. Please try again." : "Could not connect to server. Please try again.";
            return { success: false, error: detail || fallback };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutApi();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            clearTokens();
            setUser(false);
        }
    }, []);

    const refreshToken = useCallback(async () => {
        return await tryRefresh();
    }, [tryRefresh]);

    const value = useMemo(() => ({
        user, loading, login, register, logout, refreshToken, checkAuth
    }), [user, loading, login, register, logout, refreshToken, checkAuth]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// Re-export api for components that need authenticated requests
export { api };

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
